/**
 * The value the ad platforms are told a Lead is worth.
 *
 * Meta's Events Manager flags Lead events that carry no currency ("Update
 * missing or invalid currency information"), and the two ACTIVE challenge
 * campaigns are OUTCOME_LEADS optimising on lead COUNT. Sending value +
 * currency clears the diagnostic and makes the event value-shaped, which is a
 * precondition for value bidding - not, on its own, value bidding. See the
 * limit stated further down before switching any campaign goal.
 *
 * Deliberately NOT zero. A zero value clears the warning while telling Meta
 * every lead is worth nothing, and value-0 events are commonly ignored for value
 * optimisation - trading a warning for a lie.
 *
 * WHERE 1.95 COMES FROM (measured 2026-09-01, challenge_users_mirror joined to
 * revenue_payments, converted to USD via fx_rates):
 *   3,589 sign-ups from 30-120 days ago -> 70 bought (1.95%) -> $6,987.38
 *   6,987.38 / 3,589 = $1.95 expected value per lead
 *
 * MEASURE A MATURED COHORT. The obvious window - "the last 90 days" - reads
 * $0.53, less than a third of the real number, because leads who signed up last
 * week have not had time to buy yet. The same query over cohorts that HAVE had
 * time returns $1.95 (30-120d) and $2.73 (90-180d). A trailing window on a lagging
 * conversion is a systematic undercount, not a conservative estimate.
 *
 * This is an ESTIMATE IN EXPECTATION, not a per-lead truth: an individual opt-in
 * is worth $0 or a full purchase, never $1.95. It decays as conversion or price
 * moves, so re-measure it quarterly rather than trusting the constant
 * indefinitely. LEAD_VALUE_USD in the environment overrides it without a deploy.
 *
 * IT IS THE SAME NUMBER FOR EVERY LEAD, AND THAT IS THE POINT OF ITS LIMIT.
 * A constant clears Meta's "missing currency" diagnostic and makes the Lead
 * event value-shaped, but it CANNOT drive a "maximize value of conversions"
 * goal: when every lead is worth $1.95, value optimisation is arithmetically
 * identical to volume optimisation. Varying it per lead was measured and
 * rejected on 2026-09-01 - country is the only signal we hold at opt-in time,
 * over half of leads carry no country at all, and every market except the US
 * had 1-4 buyers, which is noise rather than a value signal.
 *
 * Meta events are immutable, so a Lead already sent can never be revised once
 * that lead actually buys. The realised revenue is read off Purchase instead,
 * which carries the buyer-facing value and the product content_ids.
 */
export const DEFAULT_LEAD_VALUE_USD = 1.95;
export const LEAD_VALUE_CURRENCY = "USD";

/** Effective lead value: env override, else the measured default. */
export function leadValueUsd(): number {
  // Trimmed truthiness, not `!== undefined`: the checkout island bakes this in
  // at build time and an unset var arrives as "", which Number() would turn
  // into a silent 0.
  const raw = process.env.LEAD_VALUE_USD?.trim();
  if (raw) {
    const n = Number(raw);
    // A negative or non-numeric override is a config typo. Fall back rather
    // than teaching the bidder a nonsense number.
    if (Number.isFinite(n) && n >= 0) return n;
  }
  return DEFAULT_LEAD_VALUE_USD;
}
